import { LockOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Card, Select, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import DateRangePresetPicker, {
  currentWeekPreset,
  lastNDaysPreset,
  previousWeekPreset,
  yesterdayPreset,
} from 'components/DateRangePresetPicker';

import { formatInstant } from 'scenes/RouteReport/utils/time';

import {
  TiendaPerfectaDistributor,
  TiendaPerfectaVisitHistoryNamed,
} from 'models/TiendaPerfecta';

import { FunctionsHelper } from 'utils/FunctionsHelper';

import { isRangeDayDisabled } from '../utils/filters';
import { DateRange } from '../utils/range';

const I18N_BASE = 'scenes.visit_history.filters';
// The current day is never closed (see `getDefaultRange`), so every preset
// ends at yesterday at most; one with no closed days yet is shown disabled.
// Month presets are left out on purpose: the report is reviewed by week.
const END_AT = { endAt: 'yesterday' } as const;
const RANGE_PRESETS = [
  yesterdayPreset(END_AT),
  currentWeekPreset(END_AT),
  previousWeekPreset(END_AT),
  lastNDaysPreset(7, END_AT),
  lastNDaysPreset(30, END_AT),
];

interface VisitHistoryFiltersProps {
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  zones: TiendaPerfectaVisitHistoryNamed[];
  zoneIds: string[];
  onZoneChange: (ids: string[]) => void;
  /** Already narrowed by the selected zones. */
  distributors: TiendaPerfectaDistributor[];
  distributorIds: string[];
  onDistributorChange: (ids: string[]) => void;
  /** A `low` role: the distributor is shown but cannot be changed. */
  singleDistributor: boolean;
  loading?: boolean;
  refreshing?: boolean;
  onRefresh: () => void;
  lastUpdatedAt: Date | null;
  userTimeZone: string;
}

function VisitHistoryFilters({
  range,
  onRangeChange,
  zones,
  zoneIds,
  onZoneChange,
  distributors,
  distributorIds,
  onDistributorChange,
  singleDistributor,
  loading = false,
  refreshing = false,
  onRefresh,
  lastUpdatedAt,
  userTimeZone,
}: VisitHistoryFiltersProps) {
  const { t } = useTranslation();

  const search = (input: string, option?: any) =>
    FunctionsHelper.normalizeText(option?.title ?? '').includes(
      FunctionsHelper.normalizeText(input)
    );

  return (
    <Card>
      <div className="visit-history-filters">
        <div className="visit-history-filters__left">
          <span className="visit-history-filters__group">
            <span className="visit-history-filters__label">
              {t(`${I18N_BASE}.dates`)}
            </span>
            <DateRangePresetPicker
              value={range}
              onChange={onRangeChange}
              presets={RANGE_PRESETS}
              timeZone={userTimeZone}
              // No future days (nothing was executed; "today" is the user's
              // time zone, like the URL parser) and at most 31 days from the
              // endpoint already picked, which is the contract limit. Presets
              // that break it are shown disabled.
              disabledDate={(current, picked) =>
                isRangeDayDisabled(current, picked, dayjs(), userTimeZone)
              }
            />
          </span>

          <span className="visit-history-filters__group">
            <span className="visit-history-filters__label">
              {t(`${I18N_BASE}.zones`)}
            </span>
            <Select
              mode="multiple"
              value={zoneIds}
              onChange={onZoneChange}
              disabled={zones.length === 0}
              placeholder={t(`${I18N_BASE}.all_zones`)}
              maxTagCount="responsive"
              options={zones.map((zone) => ({
                value: zone.id,
                label: zone.name,
                title: zone.name,
              }))}
              filterOption={search}
              style={{ minWidth: 190 }}
            />
          </span>

          <span className="visit-history-filters__group">
            <span className="visit-history-filters__label">
              {t(`${I18N_BASE}.distributors`)}
            </span>
            <Select
              mode="multiple"
              value={distributorIds}
              onChange={onDistributorChange}
              // Disabled but visible: hiding it would leave the view without
              // saying which distributor the routes belong to.
              disabled={singleDistributor}
              suffixIcon={singleDistributor ? <LockOutlined /> : undefined}
              placeholder={t(`${I18N_BASE}.all_distributors`)}
              maxTagCount="responsive"
              options={distributors.map((distributor) => ({
                value: distributor.id,
                label: distributor.tradeName,
                title: `${distributor.tradeName} ${distributor.code}`,
              }))}
              filterOption={search}
              style={{ minWidth: 230 }}
            />
          </span>
        </div>

        <div className="visit-history-filters__right">
          <span>{t(`${I18N_BASE}.max_days`)}</span>
          {/* The data's time, not the clock's: if a refresh fails it stops
              advancing, which is the signal that what is shown went stale. */}
          {lastUpdatedAt && (
            <span className="visit-history-filters__updated">
              {t(`${I18N_BASE}.updated_at`, {
                time: formatInstant(
                  lastUpdatedAt,
                  userTimeZone,
                  'DD-MMM HH:mm'
                ),
              })}
            </span>
          )}
          <Tooltip title={t(`${I18N_BASE}.refresh`)}>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined spin={refreshing} />}
              onClick={onRefresh}
              disabled={loading}
              aria-label={t(`${I18N_BASE}.refresh`)}
            />
          </Tooltip>
        </div>
      </div>
    </Card>
  );
}

export default VisitHistoryFilters;
